import { api } from ".";

// Types for Contract Fee Schedules
/** Summary of one contract term within a multi-year contract group (list API). */
export interface IContractTermInGroup {
  id: string;
  effectiveDate: string;
  endDate: string | null;
  status: string;
  proceduresOutdated?: boolean;
}

export interface IContractFeeSchedule {
  id: string;
  providerId: string;
  payerId: number;
  effectiveDate: string;
  endDate?: string | null;
  nextUpdateDate?: string;
  status: "Draft" | "Active" | "Inactive" | "Expired" | "Importing";
  proceduresOutdated?: boolean;
  contractGroupId?: string;
  /** Number of terms in this contract group (grouped list). */
  _termCount?: number;
  _termsInGroup?: IContractTermInGroup[];
  name?: string;
  createdAt: string;
  updatedAt: string;
  mpdrRulesetIds?: string[];
  caseRateRulesetIds?: string[];
  mpdrRulesets?: any[];
  caseRateRulesets?: any[];
  notes?: string;
  inpatientRulesets?: any[];
  inpatientRulesetIds?: string[];
  packageRulesets?: any[];
  packageRulesetIds?: string[];
  explodingFeeScheduleRulesetIds?: string[];
  explodingFeeScheduleRulesets?: any[];
  createdBy?: {
    id: string;
    email: string;
  };
  provider?: {
    id: string;
    name: string;
    classification: string;
    specialty: string;
  };
  payer?: {
    id: number;
    name: string;
    type: string;
  };
  rateCalculations?: IRateCalculation[];
  /**
   * Full carveout rows (detail GET maps Prisma `carveouts` here).
   */
  carveOutFiles?: ICarveout[];
  /**
   * List GET: lean rows `{ id, baseRateFileId }` only — used to resolve which contracts use a library carveout file.
   */
  carveouts?: Array<{ id: string; baseRateFileId?: string | null }>;
  attachments?: IAttachment[];
  medicareAdvantagePlans?: IMedicareAdvantageLink[];
  histories?: IContractHistory[];
  _count?: {
    carveouts: number;
    medicareAdvantageLinks: number;
  };
}

/** One activity-log entry for a contract (created/updated, by who, what changed). */
export interface IContractHistoryFieldChange {
  from: unknown;
  to: unknown;
}
export interface IContractHistory {
  id: string;
  type:
    | "Created"
    | "Updated"
    | "ProceduresRegenerated"
    | "AttachmentAdded"
    | "AttachmentRemoved"
    | "CarveoutAdded"
    | "CarveoutRemoved";
  contractId: string;
  userId?: string | null;
  /** Field-level diff for Updated rows: { fieldName: { from, to } }. */
  valueChanges?: Record<string, IContractHistoryFieldChange> | null;
  description?: string | null;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  } | null;
}

export type RateType = "unit" | "percent";

/** Normalize rateType from API (handles casing, null, undefined) */
export function normalizeRateType(
  raw: unknown,
  fallback: RateType = "percent"
): RateType {
  const s = String(raw ?? "").toLowerCase();
  if (s === "unit") return "unit";
  if (s === "percent") return "percent";
  return fallback;
}

export interface IRateCalculation {
  id?: string;
  baseRateFileId?: string;
  ratePercent: number;
  rateType?: RateType; // "unit" = $ (price per unit), "percent" = %
  sortOrder: number;
  baseRateFile?: {
    id: string;
    displayName: string;
    effectiveDate: string;
  };
}

/** Label for a rate line: prefer API-nested base file (all terms), then list lookup by id. */
export function resolveRateCalculationBaseRateDisplayName(
  calc: Pick<IRateCalculation, "baseRateFileId" | "baseRateFile">,
  fileList: { id: string; displayName: string }[] | undefined
): string {
  const nested = calc.baseRateFile?.displayName?.trim();
  if (nested) return nested;
  if (!calc.baseRateFileId) return "No base rate file";
  const file = fileList?.find((f) => f.id === calc.baseRateFileId);
  if (file?.displayName) return file.displayName;
  return "Base Rate File";
}

export interface ICarveout {
  id: string;
  fileName: string;
  /** Present when this carveout is linked to a library BaseRateFile (no duplicate S3 object). */
  baseRateFileId?: string;
  s3Key: string;
  fileSize: number;
  metadata?: any;
  /** % applied to the carveout's rates (mirrors rate calculations); null/undefined = 100%. */
  ratePercent?: number | null;
  rateType?: RateType;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
  };
}

/** A carveout BaseRateFile attached to a contract, with its percentage. */
export interface ICarveoutAttachment {
  baseRateFileId: string;
  ratePercent?: number;
  rateType?: RateType;
}

export interface IAttachment {
  id: string;
  fileName: string;
  s3Key: string;
  fileSize: number;
  contentType: string;
  createdAt: string;
  uploadedBy?: {
    id: string;
    name: string;
  };
}

export interface IMedicareAdvantageLink {
  medicareAdvantagePlanId: number;
  medicareAdvantagePlan?: {
    id: number;
    name: string;
    type: string;
  };
}

export interface IContractFilterParams {
  providerId?: string;
  payerId?: number;
  status?: string;
  search?: string;
  accountId?: string;
  /** Default on API is grouped; set false for a flat list of every term row. */
  groupByContractGroup?: boolean;
  effectiveDateFrom?: string;
  effectiveDateTo?: string;
}

export interface IPreviewRequest {
  cptCodes?: string[];
  limit?: number;
  carveoutOnly?: boolean;
}

export interface IPreviewResult {
  rates: IRateCalculationResult[];
  total: number;
  totalCarveouts: number;
  hasMore: boolean;
  cacheStats: {
    size: number;
    maxSize: number;
    ttl: number;
  };
}

export interface IRateCalculationResult {
  cptCode: string;
  description: string;
  providerSpecialty: string;
  baseRate: number;
  ratePercent: number;
  rateType?: RateType;
  calculatedRate: number;
  isCarveout: boolean;
  carveoutRate?: number;
  source: string;
}

/**
 * Grouped list rows often default to the Active term. When a newer Draft exists
 * (e.g. base-rate new term without roll-forward), surface that row as primary so
 * the library link, status, and Activate action target the draft.
 */
function promoteNewerDraftAsListPrimary(
  row: IContractFeeSchedule
): IContractFeeSchedule {
  // eslint-disable-next-line no-underscore-dangle
  const terms = row._termsInGroup;
  if (!terms?.length) return row;

  const draftTerms = terms.filter((t) => t.status === "Draft");
  if (draftTerms.length === 0) return row;

  const activeTerms = terms.filter((t) => t.status === "Active");
  let candidates: IContractTermInGroup[];

  if (activeTerms.length === 0) {
    candidates = draftTerms;
  } else {
    const maxActiveDate = activeTerms.reduce(
      (max, t) => (t.effectiveDate > max ? t.effectiveDate : max),
      activeTerms[0].effectiveDate
    );
    candidates = draftTerms.filter((t) => t.effectiveDate > maxActiveDate);
    if (candidates.length === 0) return row;
  }

  const pick = candidates.reduce((best, t) =>
    t.effectiveDate > best.effectiveDate ? t : best
  );

  if (pick.id === row.id) return row;

  return {
    ...row,
    id: pick.id,
    status: "Draft",
    effectiveDate: pick.effectiveDate,
    endDate: pick.endDate ?? undefined,
    proceduresOutdated: pick.proceduresOutdated,
  };
}

const contractFeeSchedulesQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    // Contract Fee Schedule CRUD
    getContractFeeSchedules: builder.query<
      IContractFeeSchedule[],
      IContractFilterParams
    >({
      query: (params) => ({
        url: "contract-fee-schedules",
        params,
      }),
      transformResponse: (
        response:
          | IContractFeeSchedule[]
          | { data: IContractFeeSchedule[]; message?: string }
      ) => {
        const raw = Array.isArray(response) ? response : response?.data ?? [];
        return raw.map(promoteNewerDraftAsListPrimary);
      },
      providesTags: ["ContractFeeSchedules"],
    }),

    getContractFeeSchedule: builder.query<
      { data: IContractFeeSchedule },
      string
    >({
      query: (id) => `contract-fee-schedules/${id}`,
      transformResponse: (response: {
        data: IContractFeeSchedule & { carveouts?: ICarveout[] };
      }) => {
        // Map backend 'carveouts' to frontend 'carveOutFiles'
        if (response.data.carveouts) {
          response.data.carveOutFiles = response.data.carveouts;
          delete (response.data as any).carveouts;
        }
        const calcs = response.data.rateCalculations;
        if (Array.isArray(calcs)) {
          response.data.rateCalculations = calcs.map((c: any) => {
            const nested = c.baseRateFile ?? c.base_rate_file;
            if (!nested) return c as IRateCalculation;
            const id = nested.id as string;
            const displayName = (nested.displayName ?? nested.display_name) as
              | string
              | undefined;
            const effectiveDate = (nested.effectiveDate ??
              nested.effective_date ??
              "") as string;
            if (!displayName) return c as IRateCalculation;
            return {
              ...c,
              baseRateFile: { id, displayName, effectiveDate },
            } as IRateCalculation;
          });
        }
        return response as { data: IContractFeeSchedule };
      },
      providesTags: (result, error, id) => [
        { type: "ContractFeeSchedules", id },
      ],
    }),

    createContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule; id: string },
      Omit<
        IContractFeeSchedule,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "provider"
        | "payer"
        | "_count"
        | "createdBy"
        | "carveOutFiles"
        | "medicareAdvantagePlans"
        | "mpdrRuleset"
        | "caseRateRuleset"
      > & {
        carveoutBaseRateFileIds?: string[];
        carveouts?: ICarveoutAttachment[];
      }
    >({
      query: (body) => ({
        url: "contract-fee-schedules",
        method: "POST",
        body: {
          ...body,
          rateCalculations: body.rateCalculations?.map((calc) => ({
            ...calc,
            ratePercent: Number(calc.ratePercent),
            rateType: calc.rateType,
            sortOrder: Number(calc.sortOrder) || 0,
          })),
        },
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    createBulkContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule[]; count: number; message: string },
      {
        providerIds: string[];
        payerIds: number[];
        effectiveDate: string;
        nextUpdateDate?: string;
        rateCalculations: RateCalculation[];
        mpdrRulesetIds?: string[];
        caseRateRulesetIds?: string[];
        medicareAdvantagePayerIds?: number[];
        copyFromContractId?: string;
        carveoutFiles?: File[];
        /** Attach library carveout BaseRateFile rows (same account), at 100%. */
        carveoutBaseRateFileIds?: string[];
        /** Attach library carveout BaseRateFile rows with a percentage (preferred). */
        carveouts?: ICarveoutAttachment[];
        notes?: string;
      }
    >({
      query: ({ carveoutFiles, ...body }) => {
        // If we have carveout files, send as multipart/form-data with TOP-LEVEL fields
        if (carveoutFiles && carveoutFiles.length > 0) {
          const formData = new FormData();

          // Top-level scalar fields
          formData.append("effectiveDate", body.effectiveDate);
          if (body.nextUpdateDate)
            formData.append("nextUpdateDate", body.nextUpdateDate);

          // Arrays as repeated fields
          (body.providerIds || []).forEach((id) =>
            formData.append("providerIds", id)
          );
          (body.payerIds || []).forEach((id) =>
            formData.append("payerIds", String(id))
          );

          // rateCalculations: send once as JSON string (backend should parse this)
          // Values normalized to numbers to mirror JSON path
          formData.append(
            "rateCalculations",
            JSON.stringify(
              (body.rateCalculations || []).map((calc, index) => ({
                baseRateFileId: calc.baseRateFileId ?? undefined,
                ratePercent: Number(calc.ratePercent),
                rateType: calc.rateType ?? "percent",
                sortOrder: Number(calc.sortOrder ?? index) || 0,
              }))
            )
          );

          // Optional arrays
          if (body.mpdrRulesetIds)
            body.mpdrRulesetIds.forEach((id) =>
              formData.append("mpdrRulesetIds", id)
            );
          if (body.caseRateRulesetIds)
            body.caseRateRulesetIds.forEach((id) =>
              formData.append("caseRateRulesetIds", id)
            );
          if (body.medicareAdvantagePayerIds)
            body.medicareAdvantagePayerIds.forEach((id) =>
              formData.append("medicareAdvantagePayerIds", String(id))
            );
          if (body.copyFromContractId)
            formData.append("copyFromContractId", body.copyFromContractId);
          if (body.notes) formData.append("notes", body.notes);
          if (body.carveoutBaseRateFileIds?.length) {
            body.carveoutBaseRateFileIds.forEach((id) =>
              formData.append("carveoutBaseRateFileIds", id)
            );
          }
          if (body.carveouts?.length) {
            formData.append(
              "carveouts",
              JSON.stringify(
                body.carveouts.map((c) => ({
                  baseRateFileId: c.baseRateFileId,
                  ratePercent:
                    c.ratePercent !== undefined
                      ? Number(c.ratePercent)
                      : undefined,
                  rateType: c.rateType,
                }))
              )
            );
          }

          // Files
          carveoutFiles.forEach((file) => {
            formData.append("carveoutFiles", file);
          });

          return {
            url: "contract-fee-schedules/bulk",
            method: "POST",
            body: formData,
          };
        }

        // Otherwise send as JSON
        return {
          url: "contract-fee-schedules/bulk",
          method: "POST",
          body: {
            ...body,
            rateCalculations: body.rateCalculations?.map((calc) => ({
              ...calc,
              ratePercent: Number(calc.ratePercent),
              rateType: calc.rateType ?? "percent",
              sortOrder: Number(calc.sortOrder) || 0,
            })),
          },
        };
      },
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    updateContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule },
      { id: string } & Omit<
        IContractFeeSchedule,
        | "id"
        | "createdAt"
        | "updatedAt"
        | "provider"
        | "payer"
        | "_count"
        | "createdBy"
        | "carveOutFiles"
        | "medicareAdvantagePlans"
        | "status"
        | "mpdrRuleset"
        | "caseRateRuleset"
      >
    >({
      query: ({ id, ...body }) => ({
        url: `contract-fee-schedules/${id}`,
        method: "PUT",
        body: {
          ...body,
          rateCalculations: body.rateCalculations?.map((calc) => ({
            ...calc,
            ratePercent: Number(calc.ratePercent),
            rateType: calc.rateType,
            sortOrder: Number(calc.sortOrder) || 0,
          })),
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ContractFeeSchedules", id },
        "ContractFeeSchedules",
      ],
    }),

    activateContractFeeSchedule: builder.mutation<
      { data: IContractFeeSchedule },
      string
    >({
      query: (id) => ({
        url: `contract-fee-schedules/${id}/activate`,
        method: "POST",
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    createNextContractTerm: builder.mutation<
      { data: IContractFeeSchedule; message?: string },
      {
        id: string;
        effectiveDate: string;
        newBaseRateFileId?: string;
      }
    >({
      query: ({ id, effectiveDate, newBaseRateFileId }) => {
        const trimmed =
          newBaseRateFileId != null ? String(newBaseRateFileId).trim() : "";
        return {
          url: `contract-fee-schedules/${id}/next-term`,
          method: "POST",
          body: {
            effectiveDate,
            ...(trimmed !== "" ? { newBaseRateFileId: trimmed } : {}),
          },
        };
      },
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    deleteContractFeeSchedule: builder.mutation<void, string>({
      query: (id) => ({
        url: `contract-fee-schedules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ContractFeeSchedules"],
    }),

    /** Removes one ContractFeeSchedule row (one term); other terms in the group stay. */
    deleteContractTerm: builder.mutation<void, string>({
      query: (termId) => ({
        url: `contract-fee-schedules/${termId}/term`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, termId) => [
        { type: "ContractFeeSchedules", id: termId },
        "ContractFeeSchedules",
      ],
    }),

    // Preview and Export
    previewFeeSchedule: builder.mutation<
      IPreviewResult,
      { contractId: string; params: IPreviewRequest }
    >({
      query: ({ contractId, params }) => ({
        url: `contract-fee-schedules/${contractId}/preview`,
        method: "POST",
        body: params,
      }),
    }),

    exportFeeSchedule: builder.mutation<
      Blob,
      { contractId: string; params: IPreviewRequest }
    >({
      query: ({ contractId, params }) => ({
        url: `contract-fee-schedules/${contractId}/export`,
        method: "POST",
        body: params,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // Carveout Management
    attachCarveoutBaseRateFiles: builder.mutation<
      { message?: string },
      { contractId: string; carveouts: ICarveoutAttachment[] }
    >({
      query: ({ contractId, carveouts }) => ({
        url: `contract-fee-schedules/${contractId}/carveouts/attach`,
        method: "POST",
        body: {
          carveouts: carveouts.map((c) => ({
            baseRateFileId: c.baseRateFileId,
            ratePercent:
              c.ratePercent !== undefined ? Number(c.ratePercent) : undefined,
            rateType: c.rateType,
          })),
        },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
        "ContractFeeSchedules",
      ],
    }),

    uploadCarveout: builder.mutation<
      ICarveout,
      {
        contractId: string;
        file: File;
        ratePercent?: number;
        rateType?: RateType;
      }
    >({
      query: ({ contractId, file, ratePercent, rateType }) => {
        const formData = new FormData();
        formData.append("file", file);
        if (ratePercent !== undefined)
          formData.append("ratePercent", String(Number(ratePercent)));
        if (rateType) formData.append("rateType", rateType);
        return {
          url: `contract-fee-schedules/${contractId}/carveouts`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    /** CLE-2573 — correct a mistyped rate on an attached carveout in place. */
    updateCarveoutRate: builder.mutation<
      ICarveout,
      {
        contractId: string;
        carveoutId: string;
        ratePercent: number;
        rateType: RateType;
      }
    >({
      query: ({ contractId, carveoutId, ratePercent, rateType }) => ({
        url: `contract-fee-schedules/${contractId}/carveouts/${carveoutId}`,
        method: "PUT",
        body: { ratePercent: Number(ratePercent), rateType },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    deleteCarveout: builder.mutation<
      void,
      { contractId: string; carveoutId: string }
    >({
      query: ({ contractId, carveoutId }) => ({
        url: `contract-fee-schedules/${contractId}/carveouts/${carveoutId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Attachment Management
    uploadAttachment: builder.mutation<
      IAttachment,
      { contractId: string; file: File }
    >({
      query: ({ contractId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `contract-fee-schedules/${contractId}/attachments`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    deleteAttachment: builder.mutation<
      void,
      { contractId: string; attachmentId: string }
    >({
      query: ({ contractId, attachmentId }) => ({
        url: `contract-fee-schedules/${contractId}/attachments/${attachmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Generate Provider Procedures
    generateProviderProcedures: builder.mutation<
      {
        success?: boolean;
        dryRun?: boolean;
        procedureCount: number;
        procedures?: any[];
        totalRate?: number;
        importedFeeScheduleId?: string;
        message?: string;
        status?: string;
        contractId?: string;
      },
      {
        contractId: string;
        options?: {
          overwriteExisting?: boolean;
          dryRun?: boolean;
        };
      }
    >({
      query: ({ contractId, options }) => ({
        url: `contract-fee-schedules/${contractId}/generate-procedures`,
        method: "POST",
        body: options || {},
      }),
      invalidatesTags: (result, error, { contractId }) => [
        { type: "ContractFeeSchedules", id: contractId },
      ],
    }),

    // Get outdated contracts
    getOutdatedContracts: builder.query<
      {
        data: IContractFeeSchedule[];
        message: string;
      },
      void
    >({
      query: () => ({
        url: "contract-fee-schedules/outdated",
      }),
      providesTags: ["ContractFeeSchedules"],
    }),

    // Regenerate provider procedures for outdated contract
    regenerateProcedures: builder.mutation<
      {
        data: {
          success: boolean;
          procedureCount: number;
          importedFeeScheduleId: string;
          message: string;
        };
        message: string;
      },
      string
    >({
      query: (contractId) => ({
        url: `contract-fee-schedules/${contractId}/regenerate-procedures`,
        method: "POST",
      }),
      invalidatesTags: (result, error, contractId) => [
        { type: "ContractFeeSchedules", id: contractId },
        "ContractFeeSchedules",
      ],
    }),
  }),
});

// Export query hooks
export const {
  useGetContractFeeSchedulesQuery,
  useLazyGetContractFeeSchedulesQuery,
  useGetContractFeeScheduleQuery,
  useCreateContractFeeScheduleMutation,
  useCreateBulkContractFeeScheduleMutation,
  useUpdateContractFeeScheduleMutation,
  useActivateContractFeeScheduleMutation,
  useCreateNextContractTermMutation,
  useDeleteContractFeeScheduleMutation,
  useDeleteContractTermMutation,
  usePreviewFeeScheduleMutation,
  useExportFeeScheduleMutation,
  useAttachCarveoutBaseRateFilesMutation,
  useUploadCarveoutMutation,
  useUpdateCarveoutRateMutation,
  useDeleteCarveoutMutation,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
  useGenerateProviderProceduresMutation,
  useGetOutdatedContractsQuery,
  useRegenerateProceduresMutation,
} = contractFeeSchedulesQueries;

// Re-export the interfaces for use in components
export type ContractFeeSchedule = IContractFeeSchedule;
export type RateCalculation = IRateCalculation;
export type ContractFilterParams = IContractFilterParams;
